import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamPeriod, ParamProjectSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { compactLines, formatDate } from "./api-formatting-nIGXUFdx.js";
import { assertProjectRefWithinConstraint } from "./project-constraints-DlfcU4mm.js";
import { formatUptimeHeadersForOutput, formatUptimeStatus, getUptimeOwnerName } from "./uptime-monitors-DdMrkrKy.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-uptime-monitor-details.ts
function formatCheck(check, monitorEnvironment) {
	const time = formatDate(check.timestamp) ?? formatDate(check.scheduledCheckTime) ?? "unknown time";
	const status = check.checkStatus ?? "unknown";
	const reason = check.checkStatusReason ? ` (${check.checkStatusReason})` : "";
	const http = check.httpStatusCode === void 0 || check.httpStatusCode === null ? "" : `, HTTP ${check.httpStatusCode}`;
	const duration = check.durationMs === void 0 ? "" : `, ${check.durationMs}ms`;
	const region = check.regionName || check.region;
	const regionPart = region ? `, ${region}` : "";
	const checkEnvironment = check.environment?.trim();
	const monitorEnv = monitorEnvironment?.trim() || null;
	const environment = checkEnvironment && checkEnvironment !== monitorEnv ? `, ${checkEnvironment}` : "";
	return `- ${time}: ${status}${reason}${http}${duration}${regionPart}${environment}`;
}
var get_uptime_monitor_details_default = defineTool({
	name: "get_uptime_monitor_details",
	skills: ["inspect"],
	requiredScopes: ["project:read"],
	description: [
		"Get details for a Sentry uptime monitor, including recent checks.",
		"",
		"Use this tool when you need to:",
		"- Inspect an uptime monitor's URL, interval, thresholds, and status",
		"- Review recent HTTP check results (success/failure, status code, duration)",
		"- Debug why an uptime monitor is failing",
		"",
		"This is separate from cron monitors (`get_monitor_details`).",
		"",
		"Request bodies are never returned. Sensitive header values are redacted.",
		"",
		"<examples>",
		"get_uptime_monitor_details(organizationSlug='my-organization', projectSlug='backend', uptimeMonitorId='12345')",
		"get_uptime_monitor_details(organizationSlug='my-organization', projectSlug='backend', uptimeMonitorId='12345', period='7d', checkLimit=20)",
		"</examples>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		projectSlug: ParamProjectSlug,
		uptimeMonitorId: z.string().trim().min(1).describe("Uptime monitor ID (detector id)."),
		period: ParamPeriod.describe("Relative time range for recent checks. Defaults to `24h` when `start` and `end` are omitted.").optional(),
		start: z.string().datetime().describe("Absolute start time. Must be provided with `end`; do not combine with `period`.").optional(),
		end: z.string().datetime().describe("Absolute end time. Must be provided with `start`; do not combine with `period`.").optional(),
		checkLimit: z.number().int().positive().max(50).describe("Maximum number of recent checks to include.").default(10)
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		setTag("project.slug", params.projectSlug);
		setTag("uptime.monitor_id", params.uptimeMonitorId);
		assertProjectRefWithinConstraint({
			resourceLabel: "Uptime monitor",
			scopedProjectSlug: context.constraints.projectSlug,
			project: { slug: params.projectSlug }
		});
		const start = params.start;
		const end = params.end;
		if (start && !end || !start && end) throw new UserInputError("`start` and `end` must be provided together.");
		const hasAbsoluteTimeRange = start !== void 0 || end !== void 0;
		const requestedPeriod = params.period?.trim() || void 0;
		if (hasAbsoluteTimeRange && requestedPeriod) throw new UserInputError("`period` cannot be combined with `start` and `end`.");
		const statsPeriod = hasAbsoluteTimeRange ? void 0 : requestedPeriod ?? "24h";
		const monitor = await apiService.getUptimeMonitorDetails({
			organizationSlug,
			projectSlug: params.projectSlug,
			uptimeMonitorId: params.uptimeMonitorId
		});
		assertProjectRefWithinConstraint({
			resourceLabel: "Uptime monitor",
			scopedProjectSlug: context.constraints.projectSlug,
			project: { slug: monitor.projectSlug }
		});
		const checks = await apiService.listUptimeMonitorChecks({
			organizationSlug,
			projectSlug: params.projectSlug,
			uptimeMonitorId: params.uptimeMonitorId,
			statsPeriod,
			start,
			end,
			limit: params.checkLimit
		});
		const webUrl = apiService.getUptimeMonitorUrl(organizationSlug, monitor.id);
		const owner = getUptimeOwnerName(monitor);
		const uptimeStatus = formatUptimeStatus(monitor.uptimeStatus);
		const output = compactLines([
			`# Uptime Monitor ${monitor.name} in **${organizationSlug}**`,
			"",
			`**ID**: ${monitor.id}`,
			`**Project**: ${monitor.projectSlug}`,
			`**Status**: ${monitor.status}`,
			`**Uptime Status**: ${uptimeStatus}`,
			`**URL**: ${monitor.url}`,
			monitor.method ? `**Method**: ${monitor.method}` : null,
			`**Interval**: ${monitor.intervalSeconds}s`,
			`**Timeout**: ${monitor.timeoutMs}ms`,
			monitor.environment ? `**Environment**: ${monitor.environment}` : null,
			owner ? `**Owner**: ${owner}` : null,
			monitor.recoveryThreshold !== void 0 && monitor.recoveryThreshold !== null ? `**Recovery Threshold**: ${monitor.recoveryThreshold}` : null,
			monitor.downtimeThreshold !== void 0 && monitor.downtimeThreshold !== null ? `**Downtime Threshold**: ${monitor.downtimeThreshold}` : null,
			monitor.traceSampling !== void 0 && monitor.traceSampling !== null ? `**Trace Sampling**: ${monitor.traceSampling}` : null,
			monitor.responseCaptureEnabled !== void 0 && monitor.responseCaptureEnabled !== null ? `**Response Capture**: ${monitor.responseCaptureEnabled}` : null,
			`**Web URL**: [Open Monitor](${webUrl})`
		]);
		const headerLines = formatUptimeHeadersForOutput(monitor.headers);
		if (headerLines.length > 0) output.push("", "## Headers", "", ...headerLines);
		if (monitor.body) output.push("", "## Request Payload", "", "Request body is configured but omitted from this view.");
		if (monitor.assertion !== void 0 && monitor.assertion !== null) output.push("", "## Assertion", "", "```json", JSON.stringify(monitor.assertion, null, 2), "```");
		output.push("", "## Recent Checks", "");
		output.push(checks.length === 0 ? "No checks found in this time range." : checks.slice(0, params.checkLimit).map((check) => formatCheck(check, monitor.environment)).join("\n"));
		output.push("", "## Response Notes", "");
		output.push(`- Search related issues with \`search_issues\` query \`uptime_rule:${monitor.id}\`.`);
		return `${output.join("\n")}\n`;
	}
});

//#endregion
export { get_uptime_monitor_details_default };
//# sourceMappingURL=get-uptime-monitor-details-DfFRiDqD.js.map